import {
  NMAxiosInst,
  SagaRegistry,
  requestHandler,
  setAlertAndLoading,
  getCurrentUser,
} from "@neumetric/blueprint-lib/store";
import { put, takeEvery, select } from "redux-saga/effects";
import { saveAs } from "file-saver";
import { auditActions } from "./slice";
import {
  mapAuditList,
  mapAuditToControl,
  mapAudit,
  buildFormDataFromPayload,
  mapReportListRow,
  mapAuditDetailsToForm,
  getCurrentAudit,
  mapControlComments,
  getCurrentControl,
  getStatusProperty,
} from "./helpers";

import Data from "./data.json";

function* fetchAuditlist({ payload } = {}) {
  const page = payload?.page ?? 1;
  const perPage = payload?.per_page ?? 10;
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get(
      `/A/audits?page=${page}&per_page=${perPage}`,
    );
    const rows = mapAuditList(res?.data || []);

    yield put(
      auditActions.setAuditList({
        rows,
        meta: res.meta,
      }),
    );
  }, "Failed to fetch audits");
}

function* fetchAudit(action) {
  const option = {
    action: action,
    errorMessage: "Failed to fetch audit",
  };
  yield requestHandler(function* () {
    const { auditId, skipControl } = action.payload;
    const temp = JSON.parse(JSON.stringify(Data)); // TODO: remove when backend is ready
    let res = yield NMAxiosInst.get(`/A/${auditId}`);
    // res.asmt_cntrls = temp.asmt_cntrls;
    // res.asmt_sections = temp.asmt_sections;
    // res.asmt_groups = temp.asmt_groups;
    if (res && res.report_type === "SOC 2 Type 2" && !res.ent_report) {
      res = yield NMAxiosInst.post("/A/create_audit_report", {
        ent_report: {
          asmt_id: res.id,
        },
      });
    }
    const currentUser = yield select(getCurrentUser);
    const mappedAudit = mapAudit(res, currentUser);
    yield put(auditActions.setAuditUploadHistory(null));
    yield put(auditActions.setCurrentAudit(mappedAudit));
    if (!skipControl) {
      if (!(mappedAudit?.asmt_cntrls && mappedAudit.asmt_cntrls.length > 0)) {
        yield put(auditActions.setCurrentControl(undefined));
      } else {
        yield put(
          auditActions.fetchControl({
            controlId: mappedAudit.asmt_cntrls[0].id,
          }),
        );
      }
    }
    return mappedAudit;
  }, option);
}

function* fetchControl({ payload }) {
  yield requestHandler(function* () {
    const { controlId } = payload;
    const res = yield NMAxiosInst.get(
      `/A/control_detail?control_id=${controlId}`,
    );
    yield put(auditActions.setCurrentControl(mapAuditToControl(res)));
  }, "Failed to fetch control");
}

function* addControlComment(action) {
  const option = {
    action,
    errorMessage: "Failed to add control comment",
  };
  yield requestHandler(function* () {
    const { controlId, comment, evidence, silent = true } = action.payload;
    const formData = new FormData();
    formData.append("control_id", controlId);
    let message = "Evidence Attached";
    if (comment) {
      message = "Comment Added";
      formData.append("auditee_resp_comment", comment);
    }
    if (evidence) {
      formData.append("evidence", evidence);
    }
    const res = yield NMAxiosInst.post(
      `/A/add_comment_evidence`,
      formData,
      NMAxiosInst.getFormDataContentType(),
    );
    const currentControl = yield select(getCurrentControl);
    if (silent) {
      yield setAlertAndLoading(false, { message });
    }
    if (!currentControl) {
      return;
    }
    const { comments, evidences } = currentControl;
    const mapped = mapControlComments([res.data]);
    yield put(
      auditActions.updateCurrentControl({
        comments: [...(comments || []), ...(mapped.comments || [])],
        evidences: [...(evidences || []), ...(mapped.evidences || [])],
      }),
    );
    if (evidence) {
      const newEvidence = {
        id: mapped.evidences[0].id,
        label: `${currentControl.title} (${mapped.evidences[0].evidence_file_name})`,
      };
      const currentAudit = yield select(getCurrentAudit);
      const evidence_list = [...(currentAudit.evidence_list || [])];
      evidence_list.push(newEvidence);
      yield put(auditActions.updateCurrentAudit({ evidence_list }));
      return newEvidence;
    }
    return mapped.comments[0];
  }, option);
}

function* fetchControlResult(action) {
  const option = {
    action,
    errorMessage: "Failed to fetch result",
  };
  yield requestHandler(function* () {
    const { controlId, result } = action.payload;
    const res = yield NMAxiosInst.post(`/A/change_include_report`, {
      result_value: result,
      asmt_ctrl_id: controlId,
    });
    return res;
  }, option);
}

function* updateControlResult(action) {
  const option = {
    action,
    errorMessage: "Failed to update result",
  };
  yield requestHandler(function* () {
    const { result, controlId } = action.payload;
    const body = { asmt_ctrl_id: controlId, asmt_cntrl: result };
    const res = yield NMAxiosInst.put(`/A/update_asmt_cntrl`, body);
    const { summary, control } = res && res.data ? res.data : {};
    if (summary) {
      const currentAudit = yield select(getCurrentAudit);
      const controls = [...(currentAudit?.asmt_cntrls || [])];
      const controlIdIndexMap = currentAudit?.controlIdIndexMap || {};
      const controlIndex = controlIdIndexMap[controlId];
      if (controlIndex > -1) {
        const currentUser = yield select(getCurrentUser);
        const statusProperty = getStatusProperty(currentAudit, currentUser);
        controls[controlIndex] = {
          ...controls[controlIndex],
          risk_score: control.risk_score,
          result_value: control.result_value,
          status: control[statusProperty] || "Pending",
        };
      }
      yield put(
        auditActions.updateCurrentAudit({ summary, asmt_cntrls: controls }),
      );
    }
    if (control) {
      yield put(auditActions.updateCurrentControl(mapAuditToControl(control)));
    }
    yield setAlertAndLoading(false, { message: "Control Evaluation updated" });
  }, option);
}

function* updateIncludeReport({ payload }) {
  yield requestHandler(function* () {
    const { controlId, auditId } = payload;
    const body = { id: auditId, include_report: true };
    yield NMAxiosInst.patch(`/A/update_report_findings`, body);
    yield put(auditActions.fetchControl({ controlId }));
    yield setAlertAndLoading(false, {
      message: "All controls included in report",
    });
  }, "Failed to update include report");
}

function* selectEvidence(action) {
  const options = {
    action,
    errorMessage: "Failed to select evidence",
  };
  yield requestHandler(function* () {
    const { controlId, evidenceId } = action.payload;
    const body = {
      evidence_id: evidenceId,
      ctrl_id: controlId,
    };
    const res = yield NMAxiosInst.post(`/A/select_evidence`, body);
    const currentControl = yield select(getCurrentControl);
    const { evidences } = currentControl;
    const mapped = mapControlComments([res.data]);
    yield put(
      auditActions.updateCurrentControl({
        evidences: [...(evidences || []), ...(mapped.evidences || [])],
      }),
    );
    yield setAlertAndLoading(false, { message: "Evidence Attached" });
  }, options);
}

function* deleteEvidence(action) {
  const option = {
    action,
    errorMessage: "Failed to delete evidence",
  };
  yield requestHandler(function* () {
    const { evidenceId } = action.payload;
    const result = yield NMAxiosInst.post(`/A/soft_delete_evidence/`, {
      evidence_id: evidenceId,
    });
    const currentControl = yield select(getCurrentControl);
    if (!currentControl) {
      return;
    }
    const evidences = [...(currentControl.evidences || [])];
    const index = evidences.findIndex((e) => e.id === result.id);
    if (index > -1) {
      evidences[index] = { ...evidences[index], is_deleted: true };
    }
    yield put(
      auditActions.updateCurrentControl({
        evidences: [...(evidences || [])],
      }),
    );
    yield setAlertAndLoading(false, { message: "Evidence Deleted" });
    return;
  }, option);
}

function* updateControlResponse(action) {
  const option = {
    action,
    errorMessage: "Failed to update control answer",
  };
  yield requestHandler(function* () {
    yield put(auditActions.setActionDisable(true));
    const currentAudit = yield select(getCurrentAudit);
    const { controlId, answer } = action.payload;
    const body = { control_id: controlId, res_id: answer };
    const res = yield NMAxiosInst.post(`/A/record_response_option`, body);
    const { summary, control } = (res && res.data) || {};
    if (summary && control) {
      const controls = [...(currentAudit?.asmt_cntrls || [])];
      const controlIdIndexMap = currentAudit?.controlIdIndexMap || {};
      const controlIndex = controlIdIndexMap[controlId];
      if (controlIndex > -1) {
        const currentUser = yield select(getCurrentUser);
        const statusProperty = getStatusProperty(currentAudit, currentUser);
        controls[controlIndex] = {
          ...controls[controlIndex],
          risk_score: control.risk_score,
          result_value: control.result_value,
          status: control[statusProperty] || "Pending",
        };
      }
      yield put(
        auditActions.updateCurrentAudit({ summary, asmt_cntrls: controls }),
      );
    }
    if (control) {
      yield put(auditActions.updateCurrentControl(mapAuditToControl(control)));
    }
    yield put(auditActions.setActionDisable(false));
    return res;
  }, option);
}
function* fetchReportFormats() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/LM/report_formats");
    yield put(auditActions.setReportFormats(res?.report_formats));
  }, "Failed to fetch Report Formats");
}

function* exportToCSV(action) {
  const option = {
    action,
    errorMessage: "Failed to export to CSV",
  };
  yield requestHandler(function* () {
    const { auditId, filename } = action.payload;
    const res = yield NMAxiosInst.post(`/A/export_csv/`, { id: auditId });
    const blob = new Blob([res], {
      type: "text/csv;charset=utf-8;",
    });
    saveAs(blob, filename);
  }, option);
}

function* downloadTemplate(action) {
  const option = {
    action,
    errorMessage: "Failed to download template",
  };
  yield requestHandler(function* () {
    const { auditId, filename } = action.payload;
    const res = yield NMAxiosInst.post(`/A/export_csv_import/`, {
      id: auditId,
    });
    const blob = new Blob([res], {
      type: "text/csv;charset=utf-8;",
    });
    saveAs(blob, filename);
  }, option);
}

function* uploadAudit(action) {
  const option = {
    action,
    errorMessage: "Failed to upload audit",
  };
  yield requestHandler(function* () {
    const { auditId, file } = action.payload;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("id", auditId);
    const res = yield NMAxiosInst.post(
      `/A/import_csv`,
      formData,
      NMAxiosInst.getFormDataContentType(),
    );
    return {
      success: res?.success_count || 0,
      failure: res?.failure_count || 0,
      action: "update",
      filename: res.status_file,
    };
  }, option);
}
function* fetchBlueprintModels() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/A/models");
    yield put(auditActions.setBlueprintModels(res?.models));
  }, "Failed to fetch blueprint Models");
}
function* fetchAuditTypes() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/A/audit_types");
    yield put(auditActions.setAuditTypes(res?.audit_types));
  }, "Failed to fetch audit types");
}
function* fetchGovernors() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/A/assessors");
    yield put(auditActions.setGovernors(res?.assessors));
  }, "Failed to fetch Governors");
}
function* fetchUsers() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/A/auditees");
    yield put(auditActions.setUsers(res?.auditees));
  }, "Failed to fetch Users");
}

// function* createAudit({ payload }) {
//   const asmt = payload?.asmt ?? payload?.payload?.asmt;
//   yield requestHandler(function* () {
//     const res = yield NMAxiosInst.post("/A", { asmt });
//     yield setAlertAndLoading(false, { message: "Audit created successfully" });
//     return res;
//   }, "Failed to create audit");
// }

function* createAudit(action) {
  const asmt = action?.payload?.asmt ?? action?.payload?.payload?.asmt;

  const options = { errorMessage: "Failed to create audit", action };

  yield requestHandler(function* () {
    try {
      const res = yield NMAxiosInst.post("/A", { asmt });

      // if backend ever returns errors in 200 response
      if (Array.isArray(res?.errors) && res.errors.length) {
        throw new Error(res.errors.join(", "));
      }

      yield setAlertAndLoading(null, {
        message: res?.message || "Audit created successfully",
      });

      if (typeof action?.onSuccess === "function") action.onSuccess(res);

      return res;
    } catch (e) {
      const backendErrors =
        e?.response?.data?.errors || e?.data?.errors || e?.errors;

      const msg =
        Array.isArray(backendErrors) && backendErrors.length
          ? backendErrors.join(", ")
          : e?.message || "Failed to create audit";

      // show backend validation error
      yield setAlertAndLoading(null, { message: msg, variant: "error" });

      throw new Error(msg);
    } finally {
      // always stop submitting spinner
      if (typeof action?.onFinally === "function") action.onFinally();
    }
  }, options);
}

// function* updateAudit(action) {
//   console.log({ action });
//   const auditId = action?.payload?.auditId;
//   const asmt = action?.payload?.payload?.asmt ?? action?.payload?.asmt;

//   const options = { errorMessage: "Failed to update audit", action };

//   yield requestHandler(function* () {
//     try {
//       const res = yield NMAxiosInst.put(`/A/${auditId}`, { asmt });
//       yield setAlertAndLoading(null, { message: "Audit updated successfully" });
//       return res;
//     } finally {
//       if (typeof action?.onComplete === "function") action.onComplete();
//     }
//   }, options);
// }

function* updateAudit(action) {
  const auditId = action?.payload?.auditId;
  const asmt = action?.payload?.payload?.asmt ?? action?.payload?.asmt;

  const options = { errorMessage: "Failed to update audit", action };

  yield requestHandler(function* () {
    try {
      const res = yield NMAxiosInst.put(`/A/${auditId}`, { asmt });

      if (Array.isArray(res?.errors) && res.errors.length) {
        throw new Error(res.errors.join(", "));
      }

      yield setAlertAndLoading(null, { message: "Audit updated successfully" });

      if (typeof action?.onSuccess === "function") action.onSuccess(res);

      return res;
    } catch (e) {
      const backendErrors = e?.response?.data?.errors || e?.data?.errors;
      const msg =
        Array.isArray(backendErrors) && backendErrors.length
          ? backendErrors.join(", ")
          : e?.message || "Failed to update audit";

      // show error alert, stay on same page
      yield setAlertAndLoading(null, { message: msg, variant: "error" });

      throw new Error(msg);
    } finally {
      // ALWAYS stop submitting spinner
      if (typeof action?.onFinally === "function") action.onFinally();
    }
  }, options);
}

function* submitAudit(action) {
  const option = {
    action: action,
    errorMessage: "Failed to submit audit",
  };
  yield requestHandler(function* () {
    const { auditId } = action.payload;
    const res = yield NMAxiosInst.post("/A/submit_audit", { id: auditId });
    yield put(
      auditActions.updateCurrentAudit({ audit_status: res.audit_status }),
    );
    yield setAlertAndLoading(false, {
      message: "Audit submitted successfully",
    });
    return res;
  }, option);
}

function* createReport(action) {
  const options = { errorMessage: "Failed to upload report", action };

  yield requestHandler(function* () {
    const p = action.payload?.formdata
      ? action.payload
      : action.payload?.payload?.formdata
        ? action.payload.payload
        : action.payload?.audit?.payload?.formdata
          ? action.payload.audit.payload
          : action.payload; // fallback

    const formData = buildFormDataFromPayload(p);

    const res = yield NMAxiosInst.post(
      "/A/report_create",
      formData,
      NMAxiosInst.getFormDataContentType(),
    );

    if (res?.id) {
      yield setAlertAndLoading(null, {
        message: "Report uploaded successfully.",
      });
      yield put(auditActions.createReportSuccess(res));
    } else {
      throw new Error(res?.error || "Failed to upload report");
    }
  }, options);
}

function* fetchReportList({ payload } = {}) {
  const page = payload?.page ?? 1;
  const perPage = payload?.per_page ?? 20;

  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get(
      `/A/report_index?page=${page}&per_page=${perPage}`,
    );

    const rows = (res?.data || []).map(mapReportListRow);

    yield put(
      auditActions.setReportList({
        rows,
        meta: {
          current_page: res?.current_page ?? page,
          per_page: res?.per_page ?? perPage,
          total_entries: res?.total_entries ?? 0,
          total_pages: res?.total_pages ?? 0,
        },
      }),
    );
  }, "Failed to fetch reports");
}

function* fetchReportFrameworkList() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get(`/A/report_frameworks`);
    yield put(auditActions.setReportFrameworkList(res?.data));
  }, "Failed to fetch report framework list");
}

function* fetchReportShow({ payload }) {
  yield requestHandler(function* () {
    const reportId = payload?.report_id;
    const res = yield NMAxiosInst.get(`/A/report_show?report_id=${reportId}`);
    if (res && res.report_format !== "SOC 2 Type 2") {
      if (res.lib_model_id) {
        res.blob = yield NMAxiosInst.getExport(`/R/${res.guid}.pdf`, {
          Accept: "application/pdf",
        });
      } else if (res.report_file_url) {
        res.blob = yield NMAxiosInst.postExport(
          "/incidents/s3_download_decrypt_file",
          { url: res.report_file_url },
        );
      }
    }
    yield put(auditActions.setCurrentReportDetails(res));
  }, "Failed to fetch report details");
}

const mapBlueprintRow = (m) => ({
  ...m,
  action: "Details",
});

function* fetchSubscribedBlueprints() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/LM/subscribed_models");
    const role = res?.role;

    const rows =
      role === "admin"
        ? (res?.models || []).map(mapBlueprintRow)
        : (res?.subscribed_models || []).map(mapBlueprintRow);

    const available =
      role === "user" ? (res?.available_models || []).map(mapBlueprintRow) : [];

    yield put(
      auditActions.setSubscribedBlueprintList({
        role: role || "user",
        rows,
        available,
      }),
    );
  }, "Failed to fetch subscribed blueprints");
}

function* fetchModelDetails({ payload }) {
  const { id } = payload;
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get(`/LM/model_details?id=${id}`);
    if (res?.success) {
      const model = res?.data || null;
      const normalized = model
        ? {
            ...model,
            controls: res?.controls ?? model.controls,
            icon: res?.icone ?? model.icon, // note: backend key is `icone`
          }
        : null;

      if (normalized) {
        yield put(auditActions.setModelDetails(normalized));
      }
    }
  }, "Failed to fetch Model Details");
}

function* fetchAuditDetails({ payload }) {
  const auditId = payload?.auditId;
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get(`/A/${auditId}`);
    const mapped = mapAuditDetailsToForm(res);

    yield put(auditActions.setCurrentAuditDetails(mapped));
  }, "Failed to fetch audit detail");
}

function* fetchAuditUploadHistory({ payload }) {
  yield requestHandler(function* () {
    const auditId = payload?.auditId;
    const res = yield NMAxiosInst.post(`/A/upload_history`, { id: auditId });
    yield put(auditActions.setAuditUploadHistory(res.uploads));
  }, "Failed to fetch audit upload history");
}

function* downloadUploadHistory(action) {
  const options = {
    action,
    errorMessage: "Failed to download upload history",
  };
  yield requestHandler(function* () {
    const { id, filename } = action.payload;
    const res = yield NMAxiosInst.post(`/A/download_upload_history`, {
      id: id,
    });
    const blob = new Blob([res], {
      type: "text/csv;charset=utf-8;",
    });
    saveAs(blob, filename);
  }, options);
}

function* shareAuditReport(action) {
  const options = {
    action,
    errorMessage: "Failed to share audit report",
  };
  yield requestHandler(function* () {
    const { reportId, emailAddresses, fileContent } = action.payload;
    yield NMAxiosInst.post(`/R/${reportId}/report_email`, {
      email_ids: emailAddresses,
      fileContent: fileContent,
    });
    yield setAlertAndLoading(null, {
      message: "Report shared successfully.",
    });
  }, options);
}
function* updateAuditStatus(action) {
  const options = {
    action,
    errorMessage: "Failed to update audit status",
  };
  yield requestHandler(function* () {
    const { auditId, status, orgId } = action.payload;
    const payload = { asmt_id: auditId, ent_org_id: orgId, state: status };
    yield NMAxiosInst.post("/consoles/update_asmt_state", payload);
    yield setAlertAndLoading(null, {
      message: "Audit status has been updated.",
    });
  }, options);
}

function* fetchReportBlueprints() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/LM/audit_report_blueprints");
    const blueprints = (res?.data || []).filter(
      (bp) => bp.status.toLowerCase() === "active",
    );
    yield put(auditActions.setReportBlueprints(blueprints));
  }, "Failed to fetch report blueprints");
}

function* createAuditReport(action) {
  const options = {
    action,
    errorMessage: "Failed to create audit report",
  };
  yield requestHandler(function* () {
    const reportData = action.payload;
    const res = yield NMAxiosInst.post("/A/create_audit_report", reportData);
    yield setAlertAndLoading(null, {
      message: "Audit report has been created.",
    });
    yield put(auditActions.fetchReportShow({ report_id: res.id }));
  }, options);
}

function* updateAuditReport(action) {
  const options = {
    action,
    errorMessage: "Failed to create audit report",
  };
  yield requestHandler(function* () {
    const reportData = action.payload;
    const res = yield NMAxiosInst.put("/A/update_audit_report", reportData);
    yield setAlertAndLoading(null, {
      message: "Audit report has been created.",
    });
    return res;
  }, options);
}

function* generateSoc2Type2Report(action) {
  const options = {
    action,
    errorMessage: "Failed to create audit report",
  };
  yield requestHandler(function* () {
    const reportData = action.payload;
    const report = yield NMAxiosInst.postExport(
      "/A/generate_audit_report",
      reportData,
    );
    return report;
  }, options);
}

SagaRegistry.register(function* auditSaga() {
  yield takeEvery("audit/fetchAuditlist", fetchAuditlist);
  yield takeEvery("audit/fetchAudit", fetchAudit);
  yield takeEvery("audit/fetchControl", fetchControl);
  yield takeEvery("audit/addControlComment", addControlComment);
  yield takeEvery("audit/fetchControlResult", fetchControlResult);
  yield takeEvery("audit/updateControlResult", updateControlResult);
  yield takeEvery("audit/updateIncludeReport", updateIncludeReport);
  yield takeEvery("audit/selectEvidence", selectEvidence);
  yield takeEvery("audit/deleteEvidence", deleteEvidence);
  yield takeEvery("audit/updateControlResponse", updateControlResponse);
  yield takeEvery("audit/fetchReportFormats", fetchReportFormats);
  yield takeEvery("audit/exportToCSV", exportToCSV);
  yield takeEvery("audit/downloadTemplate", downloadTemplate);
  yield takeEvery("audit/uploadAudit", uploadAudit);
  yield takeEvery("audit/fetchBlueprintModels", fetchBlueprintModels);
  yield takeEvery("audit/fetchAuditTypes", fetchAuditTypes);
  yield takeEvery("audit/fetchGovernors", fetchGovernors);
  yield takeEvery("audit/fetchUsers", fetchUsers);
  yield takeEvery("audit/createAudit", createAudit);
  yield takeEvery("audit/updateAudit", updateAudit);
  yield takeEvery("audit/submitAudit", submitAudit);
  yield takeEvery("audit/createReport", createReport);
  yield takeEvery("audit/fetchReportList", fetchReportList);
  yield takeEvery("audit/fetchReportShow", fetchReportShow);
  yield takeEvery("audit/fetchSubscribedBlueprints", fetchSubscribedBlueprints);
  yield takeEvery("audit/fetchModelDetails", fetchModelDetails);
  yield takeEvery("audit/fetchReportFrameworkList", fetchReportFrameworkList);
  yield takeEvery("audit/fetchAuditDetails", fetchAuditDetails);
  yield takeEvery("audit/fetchAuditUploadHistory", fetchAuditUploadHistory);
  yield takeEvery("audit/downloadUploadHistory", downloadUploadHistory);
  yield takeEvery("audit/shareAuditReport", shareAuditReport);
  yield takeEvery("audit/updateAuditStatus", updateAuditStatus);
  yield takeEvery("audit/fetchReportBlueprints", fetchReportBlueprints);
  yield takeEvery("audit/createAuditReport", createAuditReport);
  yield takeEvery("audit/updateAuditReport", updateAuditReport);
  yield takeEvery("audit/generateSoc2Type2Report", generateSoc2Type2Report);
});
